import {httpsCallable} from "firebase/functions";
import {functions} from "./config";

// Titan Cloud Functions
export const createTitanFunction = httpsCallable(functions, "createTitan");
export const updateTitanFunction = httpsCallable(functions, "updateTitan");
export const deleteTitanFunction = httpsCallable(functions, "deleteTitan");
export const getTitanStatsFunction = httpsCallable(functions, "getTitanStats");

// Session Cloud Functions
export const joinSessionFunction = httpsCallable(functions, "joinSession");
export const leaveSessionFunction = httpsCallable(functions, "leaveSession");
export const getSessionStatsFunction = httpsCallable(
  functions,
  "getSessionStats"
);
export const createSessionFunction = httpsCallable(functions, "createSession");
export const updateSessionFunction = httpsCallable(functions, "updateSession");
export const deleteSessionFunction = httpsCallable(functions, "deleteSession");
export const logSessionFunction = httpsCallable(functions, "logSession");
export const checkSessionLogStatusFunction = httpsCallable(
  functions,
  "checkSessionLogStatus"
);
export const getSessionLogFunction = httpsCallable(functions, "getSessionLog");
export const claimDMTokenFunction = httpsCallable(functions, "claimDMToken");
export const getDMTokenClaimsFunction = httpsCallable(
  functions,
  "getDMTokenClaims"
);
export const dmAddPlayerToSessionFunction = httpsCallable(
  functions,
  "dmAddPlayerToSession"
);
// Admin/Migration Cloud Functions
export const migrateSessionPlayerIdsAndDisplayNamesFunction = httpsCallable(
  functions,
  "migrateSessionPlayerIdsAndDisplayNames"
);
export const migrateSessionCharacterIdsAndPlayersFunction = httpsCallable(
  functions,
  "migrateSessionCharacterIdsAndPlayers"
);
export const migrateCharacterSheetFileNamesFunction = httpsCallable(
  functions,
  "migrateCharacterSheetFileNames"
);
export const migrateCharacterSheetPathsDryRunFunction = httpsCallable(
  functions,
  "migrateCharacterSheetPathsDryRun"
);
export const migrateCharacterSheetPathsExecuteFunction = httpsCallable(
  functions,
  "migrateCharacterSheetPathsExecute"
);

// Character Cloud Functions
export const createCharacterFunction = httpsCallable(
  functions,
  "createCharacter"
);
export const updateCharacterFunction = httpsCallable(
  functions,
  "updateCharacter"
);
export const adminUpdateCharacterFunction = httpsCallable(
  functions,
  "adminUpdateCharacter"
);
export const levelUpCharacterFunction = httpsCallable(
  functions,
  "levelUpCharacter"
);
export const getCharacterDetailsFunction = httpsCallable(
  functions,
  "getCharacterDetails"
);
export const awardExperienceFunction = httpsCallable(
  functions,
  "awardExperience"
);
export const deleteCharacterFunction = httpsCallable(
  functions,
  "deleteCharacter"
);
export const transferDMTokenToCharacterFunction = httpsCallable(
  functions,
  "transferDMTokenToCharacter"
);

// Player Cloud Functions
export const getPlayerProfileFunction = httpsCallable(
  functions,
  "getPlayerProfile"
);
export const updatePlayerProfileFunction = httpsCallable(
  functions,
  "updatePlayerProfile"
);
export const getPlayerStatisticsFunction = httpsCallable(
  functions,
  "getPlayerStatistics"
);
export const assignPlayerRoleFunction = httpsCallable(
  functions,
  "assignPlayerRole"
);

// Auth Cloud Functions (already defined in auth.ts, but keeping here for consistency)
export const setUserRoleFunction = httpsCallable(functions, "setUserRole");
export const getUserClaimsFunction = httpsCallable(functions, "getUserClaims");
export const sendWelcomeEmailFunction = httpsCallable(
  functions,
  "sendWelcomeEmail"
);

// Admin Cloud Functions
export const getSystemLogsFunction = httpsCallable(functions, "getSystemLogs");

// Type definitions for function parameters and responses
export interface CreateTitanData {
  name: string;
  description: string;
  links: string[];
  levelRange: string;
  specialties: string[];
}

export interface UpdateTitanData {
  titanId: string;
  name?: string;
  description?: string;
  links?: string[];
  levelRange?: string;
  specialties?: string[];
  isActive?: boolean;
}

export interface DeleteTitanData {
  titanId: string;
}

export interface GetTitanStatsData {
  titanId: string;
}

export interface JoinSessionData {
  sessionId: string;
  playerId: string;
  characterId: string;
}

export interface LeaveSessionData {
  sessionId: string;
  playerId: string;
}

export interface GetSessionStatsData {
  dmId?: string;
  timeRange?: "week" | "month" | "year" | "all";
}

export interface CreateSessionData {
  name: string;
  description: string;
  titanId: string;
  customWorldName?: string;
  date: Date;
  location: string;
  duration?: string;
  maxPlayers: number;
  dmId: string;
  levelRange: string;
}

export interface UpdateSessionData {
  sessionId: string;
  name?: string;
  description?: string;
  titanId?: string;
  date?: Date;
  location?: string;
  duration?: string;
  maxPlayers?: number;
  levelRange?: string;
  status?: "upcoming" | "available" | "completed";
  sessionLog?: {
    events: string;
    items: Array<{
      name: string;
      type: string;
      amount: number;
      description: string;
    }>;
  };
}

export interface DeleteSessionData {
  sessionId: string;
}

export interface LogSessionData {
  sessionId: string;
  playerId: string;
  characterId: string;
  events: string;
  currency: {
    platinum: number;
    gold: number;
    electrum: number;
    silver: number;
    copper: number;
  };
  items: Array<{
    name: string;
    quantity: number;
    description: string;
    attunement: boolean;
  }>;
}

export interface GetSessionLogData {
  sessionId: string;
  playerId: string;
  characterId: string;
}

export interface GetSessionLogResponse {
  success: boolean;
  logData: {
    events: string;
    currency: {
      platinum: number;
      gold: number;
      electrum: number;
      silver: number;
      copper: number;
    };
    items: Array<{
      name: string;
      quantity: number;
      description: string;
      attunement: boolean;
    }>;
  };
  sessionId: string;
  characterId: string;
}

// Character function data types
export interface CreateCharacterData {
  name: string;
  species: string;
  class: string;
  level: number;
  inventory?: any[];
  sheetUrl?: string;
  will?: {
    beneficiary: string;
    description: string;
  };
}

export interface UpdateCharacterData {
  characterId: string;
  updates: {
    name?: string;
    species?: string;
    class?: string;
    level?: number;

    inventory?: any[];
    currency?: {
      platinum: number;
      gold: number;
      electrum: number;
      silver: number;
      copper: number;
    };
    sheetUrl?: string;
    status?: "active" | "inactive" | "retired";
    will?: {
      beneficiary: string;
      description: string;
    };
  };
}

export interface LevelUpCharacterData {
  characterId: string;
}

export interface GetCharacterDetailsData {
  characterId: string;
}

export interface AwardExperienceData {
  characterId: string;
  experience: number;
  reason: string;
}

// Player function data types
export interface GetPlayerProfileData {
  playerId: string;
}

export interface UpdatePlayerProfileData {
  updates: {
    displayName?: string;
    bio?: string;
    preferences?: any;
  };
}

export interface GetPlayerStatisticsData {
  playerId: string;
}

export interface AssignPlayerRoleData {
  playerId: string;
  role: "player" | "dm" | "admin";
  action: "add" | "remove";
}

// System Logs function data types
export interface GetSystemLogsData {
  logTypes?: string[];
  startDate?: string;
  endDate?: string;
  userId?: string;
  characterId?: string;
  limit?: number;
  offset?: number;
}

// Function response types
export interface CreateTitanResponse {
  success: boolean;
  titanId: string;
  message: string;
}

export interface UpdateTitanResponse {
  success: boolean;
  message: string;
}

export interface DeleteTitanResponse {
  success: boolean;
  message: string;
}

export interface TitanStatsResponse {
  stats: {
    titanId: string;
    name: string;
    dmId: string;
    totalSessions: number;
    completedSessions: number;
    upcomingSessions: number;
    availableSessions: number;
    totalPlayers: number;
    averagePlayersPerSession: number;
    metadata: {
      sessionCount: number;
      totalPlayers: number;
      averageSessionRating: number;
    };
  };
}

export interface SessionStatsResponse {
  stats: {
    totalSessions: number;
    completedSessions: number;
    upcomingSessions: number;
    availableSessions: number;
    totalPlayers: number;
    averagePlayersPerSession: number;
  };
}

export interface JoinSessionResponse {
  success: boolean;
  message: string;
}

export interface LeaveSessionResponse {
  success: boolean;
  message: string;
}

export interface CreateSessionResponse {
  success: boolean;
  message?: string;
  sessionId?: string;
}

export interface UpdateSessionResponse {
  success: boolean;
  message?: string;
}

export interface DeleteSessionResponse {
  success: boolean;
  message?: string;
}

export interface LogSessionResponse {
  success: boolean;
  message: string;
  mementoTokensAwarded: number;
  downtimeDaysAwarded: number;
}

// Character function response types
export interface CreateCharacterResponse {
  success: boolean;
  characterId: string;
  message: string;
}

export interface UpdateCharacterResponse {
  success: boolean;
  message: string;
}

export interface AdminUpdateCharacterData {
  characterId: string;
  updates: {
    level?: number;
    mementoTokens?: number;
    downtimeDays?: number;
  };
}

export interface AdminUpdateCharacterResponse {
  success: boolean;
  message: string;
  updates: {
    level?: number;
    mementoTokens?: number;
    downtimeDays?: number;
    updatedAt: Date;
  };
}

export interface LevelUpCharacterResponse {
  success: boolean;
  newLevel: number;
  newTokens: number;
  tokensSpent: number;
  message: string;
}

export interface GetCharacterDetailsResponse {
  success: boolean;
  character: {
    id: string;
    name: string;
    species: string;
    class: string;
    level: number;
    playerId: string;
    mementoTokens: number;
    downtimeDays: number;
    status: string;
    inventory: any[];
    sessionsPlayed: number;
    lastPlayed: any;
    isActive: boolean;
    createdAt: any;
    updatedAt: any;
    sheetUrl?: string;
  };
  metadata: any;
  activity: any;
}

export interface AwardExperienceResponse {
  success: boolean;
  newTokens: number;
  message: string;
}

export interface SpendDowntimeDaysData {
  characterId: string;
  daysToSpend: number;
  activityDescription?: string;
}

export interface DeleteCharacterData {
  characterId: string;
  characterName: string;
}

export interface SpendDowntimeDaysResponse {
  success: boolean;
  newDowntimeDays: number;
  daysSpent: number;
  message: string;
}

export interface DeleteCharacterResponse {
  success: boolean;
  message: string;
}

export interface TransferDMTokenData {
  characterId: string;
  tokensToTransfer: number;
}

export interface TransferDMTokenResponse {
  success: boolean;
  message: string;
  newDMTokens: number;
  newCharacterTokens: number;
}

// Player function response types
export interface GetPlayerProfileResponse {
  success: boolean;
  player: {
    id: string;
    displayName: string;
    email: string;
    bio?: string;
    roles: string[];
    isActive: boolean;
    lastActive: any;
    joinDate: any;
    characters: string[];
    createdAt: any;
    updatedAt: any;
  };
  metadata: any;
  characters: any[];
  activity: any;
}

export interface UpdatePlayerProfileResponse {
  success: boolean;
  message: string;
}

export interface GetPlayerStatisticsResponse {
  success: boolean;
  statistics: {
    totalCharacters: number;
    activeCharacters: number;
    totalExperience: number;
    averageLevel: number;
    totalSessions: number;
    highestLevel: number;
    joinDate: any;
    lastActive: any;
  };
  characters: number;
  metadata: any;
}

export interface AssignPlayerRoleResponse {
  success: boolean;
  message: string;
  newRoles: string[];
}

// Migration types
export interface MigrateRequest {
  dryRun?: boolean;
}
export interface MigrateTarget {
  sessionId: string;
  changePlayerIds: boolean;
  playersToUpdate: Array<{playerId: string; from: string; to: string}>;
}
export interface MigrateResponse {
  success: boolean;
  message: string;
  checked: number;
  updated: number;
  targets?: MigrateTarget[];
}

export interface CharacterMigrateTarget {
  sessionId: string;
  changeCharacterIds: boolean;
  playersChanged: Array<{characterId: string; fields: string[]}>;
}

export interface CharacterMigrateResponse
  extends Omit<MigrateResponse, "targets"> {
  targets?: CharacterMigrateTarget[];
}

export interface CharacterSheetMigrateTarget {
  characterId: string;
  currentSheetUrl: string;
  extractedFileName: string | null;
  needsUpdate: boolean;
}

export interface CharacterSheetMigrateResponse
  extends Omit<MigrateResponse, "targets"> {
  targets?: CharacterSheetMigrateTarget[];
}

export interface CharacterSheetPathMigrateTarget extends CharacterSheetMigrateTarget {
  computedSheetPath: string | null;
}

export interface CharacterSheetPathMigrateResponse
  extends Omit<MigrateResponse, "targets"> {
  targets?: CharacterSheetPathMigrateTarget[];
  allCharactersOverview?: Array<{
    characterId: string;
    characterName: string;
    playerId: string;
    hasSheetUrl: boolean;
    hasSheetPath: boolean;
    hasFileName: boolean;
    sheetUrl: string | null;
    sheetPath: string | null;
    fileName: string | null;
    migrationStatus: 'Already Migrated' | 'Needs Migration' | 'No Sheet';
  }>;
  summary?: {
    totalCharacters: number;
    charactersWithSheetUrl: number;
    charactersWithSheetPath: number;
    charactersNeedingMigration: number;
    charactersAlreadyMigrated: number;
    charactersWithNoSheet: number;
  };
}

export interface GetSystemLogsResponse {
  success: boolean;
  logs: Array<{
    id: string;
    collection: string;
    documentId: string;
    activity: any;
    timestamp: any;
    type: string;
    description: string;
    details: any;
    userId?: string;
    characterId?: string;
    dmId?: string;
    userName?: string;
    userEmail?: string;
    characterName?: string;
    playerName?: string;
  }>;
  total: number;
  hasMore: boolean;
}

// Function exports
export const spendDowntimeDaysFunction = httpsCallable<
  SpendDowntimeDaysData,
  SpendDowntimeDaysResponse
>(functions, "spendDowntimeDays");

export const transferDMTokenToCharacterTypedFunction = httpsCallable<
  TransferDMTokenData,
  TransferDMTokenResponse
>(functions, "transferDMTokenToCharacter");
